import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { handleError } from "@/lib/errorHandler";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { DashboardNav } from "@/components/layout/DashboardNav";

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  schema_data: any;
  sample_data: any[];
}

const Templates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .order("category");

      if (error) throw error;
      setTemplates((data || []) as Template[]);
    } catch (error: any) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const duplicateTemplate = async (template: Template) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setDuplicating(template.id);

    try {
      // Get or create default workspace
      let workspaceId: string;
      
      const { data: existingWorkspaces } = await supabase
        .from("workspaces")
        .select("id")
        .eq("owner_id", user.id)
        .limit(1);

      if (existingWorkspaces && existingWorkspaces.length > 0) {
        workspaceId = existingWorkspaces[0].id;
      } else {
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .insert([{ name: "My Workspace", owner_id: user.id }])
          .select()
          .single();

        if (workspaceError) throw workspaceError;
        
        await supabase
          .from("workspace_members")
          .insert([{ workspace_id: workspace.id, user_id: user.id, role: "owner" }]);
        
        workspaceId = workspace.id;
      }

      // Create base
      const { data: base, error: baseError } = await supabase
        .from("bases")
        .insert([{
          workspace_id: workspaceId,
          name: template.name,
          description: template.description,
          icon: template.icon,
          created_by: user.id,
        }])
        .select()
        .single();

      if (baseError) throw baseError;

      // Create tables and fields from schema
      const schema = template.schema_data;
      const tableMap: Record<string, string> = {};
      
      for (const tableSchema of schema.tables || []) {
        const { data: table, error: tableError } = await supabase
          .from("tables")
          .insert([{
            base_id: base.id,
            name: tableSchema.name,
            description: tableSchema.description,
          }])
          .select()
          .single();

        if (tableError) throw tableError;
        tableMap[tableSchema.name] = table.id;

        // Create fields
        for (const fieldSchema of tableSchema.fields || []) {
          await supabase.from("fields").insert([{
            table_id: table.id,
            name: fieldSchema.name,
            type: fieldSchema.type,
            config: fieldSchema.config || {},
            order_index: fieldSchema.order_index || 0,
            is_required: fieldSchema.is_required || false,
          }]);
        }
      }

      // Insert sample data
      for (const record of template.sample_data || []) {
        const tableId = tableMap[record.table];
        if (tableId) {
          await supabase.from("records").insert([{
            table_id: tableId,
            data: record.data,
            created_by: user.id,
          }]);
        }
      }

      // Create automations from template if they exist
      const automations = schema.automations || [];
      for (const automation of automations) {
        await supabase.from("automations").insert([{
          workspace_id: workspaceId,
          name: automation.name,
          description: automation.description,
          trigger_config: automation.trigger,
          action_config: automation.actions,
          status: "active",
          created_by: user.id,
        }]);
      }

      toast.success("Template created successfully!");
      navigate(`/base/${base.id}`);
    } catch (error: any) {
      handleError(error, "Failed to create from template");
    } finally {
      setDuplicating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const categories = Array.from(new Set(templates.map((t) => t.category)));

  return (
    <div className="min-h-screen bg-background pb-mobile-nav">
      <div className="hidden md:block">
        <DashboardNav />
      </div>
      
      {/* Mobile Header */}
      <nav className="md:hidden border-b border-border bg-card sticky top-0 z-40">
        <div className="flex items-center h-14 px-4 gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-bold text-lg">Templates</h1>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-6 md:py-12">
        <div className="hidden md:block mb-8">
          <h1 className="text-3xl font-bold mb-2">Template Gallery</h1>
          <p className="text-muted-foreground">Choose a template to get started quickly</p>
        </div>

        {categories.map((category) => (
          <div key={category} className="mb-8 md:mb-12">
            <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">{category}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {templates
                .filter((t) => t.category === category)
                .map((template) => (
                  <Card key={template.id} className="hover:shadow-lg transition-all">
                    <CardHeader className="p-4 md:p-6">
                      <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
                        <span className="text-2xl md:text-3xl">{template.icon}</span>
                        <span className="truncate">{template.name}</span>
                      </CardTitle>
                      <CardDescription className="text-sm line-clamp-2">{template.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
                      <div className="text-xs text-muted-foreground mb-3">
                        {template.schema_data?.tables?.length || 0} tables • {template.schema_data?.automations?.length || 0} automations
                      </div>
                      <Button
                        onClick={() => duplicateTemplate(template)}
                        disabled={duplicating === template.id}
                        className="w-full touch-target"
                      >
                        {duplicating === template.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            Use Template
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No templates available yet.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default Templates;
